import { uiText, useUiLanguage } from '../../utils/uiTranslation';
/**
 * PublicManpowerForm.js
 * Public form for external department managers to submit hiring requests
 * Accessible via permanent URL with domain restriction security
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './ManpowerRequest.css';

const PublicManpowerForm = () => {
  useUiLanguage();
    const { slug } = useParams();

    const departmentOptions = [
        'Administration',
        'Customer Support',
        'Engineering',
        'Product Management',
        'Design / UX',
        'Data & Analytics',
        'Marketing',
        'Sales',
        'Finance & Accounting',
        'Human Resources',
        'Operations',
        'IT / Systems',
        'Security / InfoSec',
        'Legal',
        'Compliance',
        'Procurement',
        'Supply Chain',
        'Logistics',
        'Manufacturing',
        'Quality Assurance',
        'Research & Development',
        'Business Development',
        'Partnerships',
        'Strategy',
        'Content',
        'Communications / PR',
        'Community',
        'Facilities',
        'Real Estate',
        'Construction',
        'Healthcare',
        'Education'
    ];

    // Loading and error states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState('');

    // Company data
    const [companyData, setCompanyData] = useState({
        companyName: '',
        logoUrl: ''
    });
    const [settings, setSettings] = useState({
        departments: [],
        locations: [],
        gradeCodes: [],
        allowedDomains: [],
        emailWhitelist: [],
        requireInternalApproval: false,
        formFields: null // Will be loaded from Firebase
    });

    // Helper to check if a field is enabled (defaults to true if no config)
    const isFieldEnabled = (fieldId) => {
        if (!settings.formFields) return true; // Default: show all fields
        const field = settings.formFields.find(f => f.id === fieldId);
        return field ? field.enabled : true;
    };

    // Helper to check if a field is required
    const isFieldRequired = (fieldId) => {
        if (!settings.formFields) return false; // Default requirement handled in JSX
        const field = settings.formFields.find(f => f.id === fieldId);
        return field ? field.required : false;
    };

    // Form data - Requester Info
    const [requesterName, setRequesterName] = useState('');
    const [requesterEmail, setRequesterEmail] = useState('');
    const [requesterDepartment, setRequesterDepartment] = useState('');
    const [requesterLocation, setRequesterLocation] = useState('');

    // Form data - Request Type
    const [requestType, setRequestType] = useState('new'); // 'new' or 'replacement'
    const [replacementName, setReplacementName] = useState('');
    const [budgetStatus, setBudgetStatus] = useState('');

    // Form data - Position Details
    const [jobTitle, setJobTitle] = useState('');
    const [headcount, setHeadcount] = useState(1);
    const [employmentType, setEmploymentType] = useState('full-time');
    const [gradeCode, setGradeCode] = useState('');
    const [salaryRange, setSalaryRange] = useState('');

    // Form data - GCC Compliance
    const [nationalityPref, setNationalityPref] = useState('');
    const [visaRequirement, setVisaRequirement] = useState('');

    // Form data - Urgency & Justification
    const [urgency, setUrgency] = useState('');
    const [justification, setJustification] = useState('');
    const [costCenter, setCostCenter] = useState('');

    // Form data - Optional
    const [skills, setSkills] = useState('');
    const [experienceLevel, setExperienceLevel] = useState('');
    const [reportingTo, setReportingTo] = useState('');

    // Email validation helper
    const validateEmailDomain = (email) => {
        if (!email) return false;
        const domain = email.split('@')[1]?.toLowerCase();
        if (!domain) return false;

        // Check if in whitelist
        if (settings.emailWhitelist.some(e => e.toLowerCase() === email.toLowerCase())) {
            return true;
        }

        // Check if domain is allowed
        if (settings.allowedDomains.some(d => domain === d.toLowerCase() || domain.endsWith('.' + d.toLowerCase()))) {
            return true;
        }

        return false;
    };

    // Load company data on mount
    useEffect(() => {
        const loadCompanyData = async () => {
            try {
                if (!slug) {
                    setError(uiText('Invalid request link.'));
                    setLoading(false);
                    return;
                }
                const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
                if (!BACKEND_URL) {
                    setError(uiText('Form service is not configured.'));
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${BACKEND_URL}/manpower/public-form-config`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ company_slug: slug })
                });

                const data = await response.json();
                if (!response.ok || !data.success) {
                    setError(data.error || 'Failed to load form. Please try again later.');
                    setLoading(false);
                    return;
                }

                setCompanyData({
                    companyName: data.company_name || 'Company',
                    logoUrl: data.logo_url || ''
                });
                setSettings({
                    departments: data.settings?.departments || [],
                    locations: data.settings?.locations || [],
                    gradeCodes: data.settings?.gradeCodes || [],
                    allowedDomains: data.settings?.allowedDomains || [],
                    emailWhitelist: data.settings?.emailWhitelist || [],
                    requireInternalApproval: data.settings?.requireInternalApproval === true,
                    approverEmails: data.settings?.approverEmails || [],
                    formFields: data.settings?.formFields || null
                });

                setLoading(false);
            } catch (err) {
                console.error('Error loading company data:', err);
                setError(uiText('Failed to load form. Please try again later.'));
                setLoading(false);
            }
        };

        loadCompanyData();
    }, [slug]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!requesterName.trim()) {
            alert(uiText('Please enter your name.'));
            return;
        }
        if (!requesterEmail.trim()) {
            alert(uiText('Please enter your email.'));
            return;
        }
        if (!validateEmailDomain(requesterEmail)) {
            alert(uiText('Your email domain is not authorized to submit requests. Please use your company email or contact HR.'));
            return;
        }
        if (!requesterDepartment) {
            alert(uiText('Please select your department.'));
            return;
        }
        if (!jobTitle.trim()) {
            alert(uiText('Please enter the job title.'));
            return;
        }
        if (!urgency) {
            alert(uiText('Please select the urgency level.'));
            return;
        }
        if (!justification.trim()) {
            alert(uiText('Please provide a business justification.'));
            return;
        }
        if (requestType === 'new' && !budgetStatus) {
            alert(uiText('Please select the budget status for this new position.'));
            return;
        }
        if (requestType === 'replacement' && !replacementName.trim()) {
            alert(uiText('Please enter the name of the employee being replaced.'));
            return;
        }

        setSubmitting(true);

        try {
            // Check rate limiting (simple client-side check, server should also validate)
            // For now, just proceed - rate limiting can be checked on read

            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;
            if (!BACKEND_URL) {
                alert(uiText('Submission service is not configured. Please contact support.'));
                return;
            }

            const payload = {
                company_slug: slug,
                requester_name: requesterName,
                requester_email: requesterEmail,
                requester_department: requesterDepartment,
                requester_location: requesterLocation || null,
                request_type: requestType,
                replacement_name: replacementName,
                budget_status: budgetStatus,
                job_title: jobTitle,
                headcount: parseInt(headcount, 10) || 1,
                employment_type: employmentType,
                grade_code: gradeCode,
                salary_range: salaryRange,
                nationality_pref: nationalityPref,
                visa_requirement: visaRequirement,
                urgency,
                justification,
                cost_center: costCenter,
                skills,
                experience_level: experienceLevel,
                reporting_to: reportingTo
            };

            const response = await fetch(`${BACKEND_URL}/manpower/submit-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                alert(data.error || 'Failed to submit request. Please try again.');
                return;
            }

            setSubmissionStatus(data.status || '');
            setSubmitted(true);
        } catch (err) {
            console.error('Error submitting request:', err);
            alert(uiText('Failed to submit request. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="manpower-form-loading">
                <div className="manpower-form-spinner" />
                <p>{uiText("Loading form...")}</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="manpower-form-page">
                <div className="manpower-form-container">
                    <div className="manpower-form-error">
                        <div className="error-icon">
                            <i className="fa-solid fa-exclamation-triangle" />
                        </div>
                        <h2>{uiText("Unable to Load Form")}</h2>
                        <p>{uiText(error)}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (submitted) {
        const requiresInternalApproval = submissionStatus === 'pending_internal';
        return (
            <div className="manpower-form-page">
                <div className="manpower-form-container">
                    <div className="manpower-form-success">
                        <div className="success-icon">
                            <i className="fa-solid fa-check" />
                        </div>
                        <h2>{uiText("Request Submitted!")}</h2>
                        <p>{uiText("Your manpower request has been submitted successfully.")}{requiresInternalApproval
                                ? uiText(" It will be reviewed by Finance/GM before reaching HR.")
                                : uiText(" HR will review your request and get back to you.")}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Main form
    return (
        <div className="manpower-form-page">
            <div className="manpower-form-container">
                <div className="manpower-form-header">
                    {companyData.logoUrl && (
                        <img src={companyData.logoUrl} alt="" className="company-logo" />
                    )}
                    <h1>{uiText("Request a New Hire")}</h1>
                    <p>{companyData.companyName}</p>
                </div>

                <form className="manpower-form-body" onSubmit={handleSubmit}>
                    {/* Requester Information */}
                    <div className="manpower-form-section">
                        <h3>{uiText("Your Information")}</h3>
                        <div className="manpower-form-row">
                            <div className="manpower-form-group">
                                <label>{uiText("Your Name ")}<span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={requesterName}
                                    onChange={(e) => setRequesterName(e.target.value)}
                                    placeholder={uiText("Enter your full name")}
                                />
                            </div>
                            <div className="manpower-form-group">
                                <label>{uiText("Your Email ")}<span className="required">*</span></label>
                                <input
                                    type="email"
                                    value={requesterEmail}
                                    onChange={(e) => setRequesterEmail(e.target.value)}
                                    placeholder="you@company.com"
                                />
                            </div>
                        </div>
                        <div className="manpower-form-row">
                            <div className="manpower-form-group">
                                <label>{uiText("Your Department ")}<span className="required">*</span></label>
                                <select
                                    value={requesterDepartment}
                                    onChange={(e) => setRequesterDepartment(e.target.value)}
                                >
                                    <option value="">{uiText("Select department...")}</option>
                                    {departmentOptions.map((dept, i) => (
                                        <option key={i} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>
                            {settings.locations.length > 0 && (
                                <div className="manpower-form-group">
                                    <label>{uiText("Location / Property")}</label>
                                    <select
                                        value={requesterLocation}
                                        onChange={(e) => setRequesterLocation(e.target.value)}
                                    >
                                        <option value="">{uiText("Select location...")}</option>
                                        {settings.locations.map((loc, i) => (
                                            <option key={i} value={loc}>{loc}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Request Type */}
                    <div className="manpower-form-section">
                        <h3>{uiText("Request Type")}</h3>
                        <div className="manpower-form-group">
                            <label>{uiText("Is this a new position or a replacement? ")}<span className="required">*</span></label>
                            <div className="manpower-radio-group">
                                <button
                                    type="button"
                                    className={`manpower-radio-option ${requestType === 'new' ? 'selected' : ''}`}
                                    onClick={() => setRequestType('new')}
                                    aria-pressed={requestType === 'new'}
                                >
                                    <span>{uiText("New Position")}</span>
                                </button>
                                <button
                                    type="button"
                                    className={`manpower-radio-option ${requestType === 'replacement' ? 'selected' : ''}`}
                                    onClick={() => setRequestType('replacement')}
                                    aria-pressed={requestType === 'replacement'}
                                >
                                    <span>{uiText("Replacement")}</span>
                                </button>
                            </div>
                        </div>

                        {requestType === 'replacement' && (
                            <div className="manpower-form-group">
                                <label>{uiText("Employee Being Replaced ")}<span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={replacementName}
                                    onChange={(e) => setReplacementName(e.target.value)}
                                    placeholder={uiText("Enter the name of the departing employee")}
                                />
                            </div>
                        )}

                        {requestType === 'new' && (
                            <div className="manpower-form-group">
                                <label>{uiText("Budget Status ")}<span className="required">*</span></label>
                                <select
                                    value={budgetStatus}
                                    onChange={(e) => setBudgetStatus(e.target.value)}
                                >
                                    <option value="">{uiText("Select budget status...")}</option>
                                    <option value="budgeted">{uiText("Budgeted")}</option>
                                    <option value="unbudgeted">{uiText("Unbudgeted")}</option>
                                    <option value="pending">{uiText("Pending Finance Approval")}</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Position Details */}
                    <div className="manpower-form-section">
                        <h3>{uiText("Position Details")}</h3>
                        <div className="manpower-form-row">
                            <div className="manpower-form-group">
                                <label>{uiText("Job Title ")}<span className="required">*</span></label>
                                <input
                                    type="text"
                                    value={jobTitle}
                                    onChange={(e) => setJobTitle(e.target.value)}
                                    placeholder={uiText("e.g., Software Engineer")}
                                />
                            </div>
                            <div className="manpower-form-group">
                                <label>{uiText("Number of Positions ")}<span className="required">*</span></label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={headcount}
                                    onChange={(e) => setHeadcount(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="manpower-form-group">
                            <label>{uiText("Employment Type ")}<span className="required">*</span></label>
                            <div className="manpower-radio-group">
                                {['full-time', 'part-time', 'contract'].map((type) => (
                                    <button
                                        type="button"
                                        key={type}
                                        className={`manpower-radio-option ${employmentType === type ? 'selected' : ''}`}
                                        onClick={() => setEmploymentType(type)}
                                        aria-pressed={employmentType === type}
                                    >
                                        <span>{type.charAt(0).toUpperCase() + type.slice(1).replace('-', '-')}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="manpower-form-row">
                            {settings.gradeCodes.length > 0 && (
                                <div className="manpower-form-group">
                                    <label>{uiText("Grade / Band Code")}</label>
                                    <select
                                        value={gradeCode}
                                        onChange={(e) => setGradeCode(e.target.value)}
                                    >
                                        <option value="">{uiText("Select grade...")}</option>
                                        {settings.gradeCodes.map((code, i) => (
                                            <option key={i} value={code}>{code}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {isFieldEnabled('salaryRange') && (
                                <div className="manpower-form-group">
                                    <label>{uiText("Salary Range (if known)")}</label>
                                    <input
                                        type="text"
                                        value={salaryRange}
                                        onChange={(e) => setSalaryRange(e.target.value)}
                                        placeholder="e.g., $50,000 - $70,000"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Visa & Nationality - only show if either field is enabled */}
                    {(isFieldEnabled('nationalityPref') || isFieldEnabled('visaRequirement')) && (
                        <div className="manpower-form-section">
                            <h3>{uiText("Visa & Nationality")}</h3>
                            <div className="manpower-form-row">
                                {isFieldEnabled('nationalityPref') && (
                                    <div className="manpower-form-group">
                                        <label>{uiText("Nationality Preference")}</label>
                                        <select
                                            value={nationalityPref}
                                            onChange={(e) => setNationalityPref(e.target.value)}
                                        >
                                            <option value="">{uiText("No preference")}</option>
                                            <option value="local">{uiText("Local Candidates Preferred")}</option>
                                            <option value="any">{uiText("Open to All Nationalities")}</option>
                                            <option value="specific">{uiText("Specific Nationality Required")}</option>
                                        </select>
                                    </div>
                                )}
                                {isFieldEnabled('visaRequirement') && (
                                    <div className="manpower-form-group">
                                        <label>{uiText("Visa Status Requirement")}</label>
                                        <select
                                            value={visaRequirement}
                                            onChange={(e) => setVisaRequirement(e.target.value)}
                                        >
                                            <option value="">{uiText("Any")}</option>
                                            <option value="local">{uiText("Local Hire Only (No Sponsorship Needed)")}</option>
                                            <option value="transfer">{uiText("Work Permit Transfer Acceptable")}</option>
                                            <option value="new">{uiText("New Work Permit / Sponsorship Required")}</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Urgency & Justification */}
                    <div className="manpower-form-section">
                        <h3>{uiText("Urgency & Justification")}</h3>
                        <div className="manpower-form-group">
                            <label>{uiText("Urgency ")}<span className="required">*</span></label>
                            <div className="manpower-radio-group">
                                {[
                                    { value: 'immediate', label: 'Immediate' },
                                    { value: '30_days', label: 'Within 30 Days' },
                                    { value: '90_days', label: 'Within 90 Days' },
                                    { value: 'not_urgent', label: 'Not Urgent' }
                                ].map((opt) => (
                                    <button
                                        type="button"
                                        key={opt.value}
                                        className={`manpower-radio-option ${urgency === opt.value ? 'selected' : ''}`}
                                        onClick={() => setUrgency(opt.value)}
                                        aria-pressed={urgency === opt.value}
                                    >
                                        <span>{uiText(opt.label)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="manpower-form-group">
                            <label>{uiText("Business Justification ")}<span className="required">*</span></label>
                            <textarea
                                value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                placeholder={uiText("Explain why this hire is needed, what the role will accomplish, and any relevant context...")}
                            />
                        </div>

                        <div className="manpower-form-group">
                            <label>{uiText("Cost Center / Budget Code")}</label>
                            <input
                                type="text"
                                value={costCenter}
                                onChange={(e) => setCostCenter(e.target.value)}
                                placeholder={uiText("e.g., CC-2024-PT")}
                            />
                        </div>
                    </div>

                    {/* Optional Details - only show if any optional field is enabled */}
                    {(isFieldEnabled('skills') || isFieldEnabled('experienceLevel') || isFieldEnabled('reportingTo')) && (
                        <div className="manpower-form-section">
                            <h3>{uiText("Additional Details (Optional)")}</h3>
                            {isFieldEnabled('skills') && (
                                <div className="manpower-form-group">
                                    <label>{uiText("Required Skills")}</label>
                                    <input
                                        type="text"
                                        value={skills}
                                        onChange={(e) => setSkills(e.target.value)}
                                        placeholder={uiText("e.g., Project Management, Excel, Python")}
                                    />
                                </div>
                            )}
                            <div className="manpower-form-row">
                                {isFieldEnabled('experienceLevel') && (
                                    <div className="manpower-form-group">
                                        <label>{uiText("Experience Level")}</label>
                                        <select
                                            value={experienceLevel}
                                            onChange={(e) => setExperienceLevel(e.target.value)}
                                        >
                                            <option value="">{uiText("Select level...")}</option>
                                            <option value="entry">{uiText("Entry Level (0-2 years)")}</option>
                                            <option value="mid">{uiText("Mid Level (3-5 years)")}</option>
                                            <option value="senior">{uiText("Senior (6-10 years)")}</option>
                                            <option value="expert">{uiText("Expert (10+ years)")}</option>
                                        </select>
                                    </div>
                                )}
                                {isFieldEnabled('reportingTo') && (
                                    <div className="manpower-form-group">
                                        <label>{uiText("Reports To")}</label>
                                        <input
                                            type="text"
                                            value={reportingTo}
                                            onChange={(e) => setReportingTo(e.target.value)}
                                            placeholder={uiText("e.g., Property Manager")}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="manpower-form-submit"
                        disabled={submitting}
                    >
                        {submitting ? uiText("Submitting...") : uiText("Submit Request")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PublicManpowerForm;
